import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, X, Save, Upload, Users } from 'lucide-react';
import { CompanyPosition, ProfileSource, ResolvedProfile, PositionMemberAssignment } from '@/types/positionAnalysisTypes';
import { profileTypeMapping } from '@/utils/profileTypeMapping';
import { standardProfileToRanges } from '@/utils/positionFitAnalysis';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import SearchablePersonSelect from '@/components/SearchablePersonSelect';
import { getScoreValue } from '@/utils/scoreCalculations';
import { sigmaToPercentile } from '@/utils/personalityCalculations';

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
}

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  assessment_variant?: string | null;
  created_at?: string;
}

interface PositionBuilderProps {
  positions: CompanyPosition[];
  onPositionsChange: (positions: CompanyPosition[]) => void;
  availableMembers: SurveySubmission[];
  onAssignmentChange: (positionId: string, members: PositionMemberAssignment[]) => void;
}

export const PositionBuilder = ({ 
  positions, 
  onPositionsChange, 
  availableMembers, 
  onAssignmentChange 
}: PositionBuilderProps) => {
  const [idealProfiles, setIdealProfiles] = useState<IdealProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [configName, setConfigName] = useState('');
  const [savedConfigs, setSavedConfigs] = useState<string[]>([]);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [configToReplace, setConfigToReplace] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchIdealProfiles();
  }, []);

  const fetchIdealProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('ideal_profiles')
        .select('*')
        .order('name');

      if (error) throw error;
      setIdealProfiles(data || []);
    } catch (error) {
      console.error('Error fetching ideal profiles:', error);
      toast.error('Failed to load ideal profiles');
    } finally {
      setLoading(false);
    }
  };

  const addPosition = () => {
    const newPosition: CompanyPosition = {
      id: crypto.randomUUID(),
      name: '',
      profileSource: 'standard',
      idealProfileId: null,
      standardProfileKey: null,
      resolvedProfile: null,
      assignedMembers: []
    };
    onPositionsChange([...positions, newPosition]);
  };

  const removePosition = (positionId: string) => {
    onPositionsChange(positions.filter(p => p.id !== positionId));
  };

  const updatePosition = (positionId: string, updates: Partial<CompanyPosition>) => {
    onPositionsChange(positions.map(p => p.id === positionId ? { ...p, ...updates } : p));
  };

  const handleProfileSourceChange = (positionId: string, source: ProfileSource) => {
    updatePosition(positionId, {
      profileSource: source,
      idealProfileId: null,
      standardProfileKey: null,
      resolvedProfile: null
    });
  };

  const handleIdealProfileSelect = (positionId: string, profileId: string) => {
    const profile = idealProfiles.find(p => p.id === profileId);
    if (profile) {
      const resolved: ResolvedProfile = {
        name: profile.name,
        dominance_min: profile.dominance_min,
        dominance_max: profile.dominance_max,
        extraversion_min: profile.extraversion_min,
        extraversion_max: profile.extraversion_max,
        patience_min: profile.patience_min,
        patience_max: profile.patience_max,
        formality_min: profile.formality_min,
        formality_max: profile.formality_max
      };
      updatePosition(positionId, {
        idealProfileId: profileId,
        resolvedProfile: resolved
      });
    }
  };

  const handleStandardProfileSelect = (positionId: string, profileKey: string) => {
    const resolved = standardProfileToRanges(profileKey);
    updatePosition(positionId, {
      standardProfileKey: profileKey,
      resolvedProfile: resolved
    });
  };

  // Member assignment logic
  const getAssignedMemberIds = (): string[] => {
    return positions.flatMap(pos => pos.assignedMembers.map(m => m.memberId));
  };

  const addMemberToPosition = (positionId: string) => {
    const memberId = selectedMemberIds[positionId];
    if (!memberId) return;

    const member = availableMembers.find(m => m.id === memberId);
    if (!member) return;

    const position = positions.find(p => p.id === positionId);
    if (!position) return;

    const alreadyAssigned = position.assignedMembers.some(m => m.memberId === memberId);
    if (alreadyAssigned) return;

    // For M3 assessments, use stored percentiles directly; otherwise convert sigma via sigmaToPercentile
    const isM3 = member.scores?.selfBehavior?.scores || member.assessment_variant === 'model3';
    const selfScores = member.scores?.selfBehavior?.scores || member.scores;

    const getTraitScore = (trait: string): number => {
      // Prefer stored percentile (M3)
      const percentileVal = selfScores?.[`${trait}Percentile`];
      if (percentileVal !== undefined && percentileVal !== null && !isNaN(Number(percentileVal))) {
        return Number(percentileVal);
      }
      // Fall back to raw sigma value (already what getScoreValue extracts)
      const raw = getScoreValue(member.scores, trait) ?? 0;
      // positionFitAnalysis.ts calls sigmaToPercentile on these, so pass raw sigma
      return raw;
    };

    // If we got percentiles directly, we need to tell positionFitAnalysis NOT to convert again.
    // The cleanest approach: always pass percentiles here, and skip conversion in positionFitAnalysis.
    const hasStoredPercentiles = selfScores?.dominancePercentile !== undefined;

    let dominance: number, extraversion: number, patience: number, formality: number;
    if (hasStoredPercentiles) {
      // Pass percentiles directly — positionFitAnalysis will convert via sigmaToPercentile,
      // so we need to pass raw sigma. But we don't have sigma for M3.
      // Instead, store percentiles and mark them so positionFitAnalysis can skip conversion.
      dominance = Number(selfScores.dominancePercentile ?? 50);
      extraversion = Number(selfScores.extraversionPercentile ?? 50);
      patience = Number(selfScores.patiencePercentile ?? 50);
      formality = Number(selfScores.formalityPercentile ?? 50);
    } else {
      dominance = getScoreValue(member.scores, 'dominance') ?? 0;
      extraversion = getScoreValue(member.scores, 'extraversion') ?? 0;
      patience = getScoreValue(member.scores, 'patience') ?? 0;
      formality = getScoreValue(member.scores, 'formality') ?? 0;
    }

    const newMember: PositionMemberAssignment = {
      memberId: member.id,
      memberName: member.full_name,
      email: member.email,
      profileName: member.profile_name,
      scores: {
        dominance,
        extraversion,
        patience,
        formality
      },
      scoresArePercentiles: hasStoredPercentiles
    };

    onAssignmentChange(positionId, [...position.assignedMembers, newMember]);
    setSelectedMemberIds(prev => ({ ...prev, [positionId]: '' }));
  };

  const removeMemberFromPosition = (positionId: string, memberId: string) => {
    const position = positions.find(p => p.id === positionId);
    if (!position) return;

    onAssignmentChange(
      positionId,
      position.assignedMembers.filter(m => m.memberId !== memberId)
    );
  };

  const loadSavedConfigNames = () => {
    const configsJson = localStorage.getItem('company2_configs');
    if (configsJson) {
      const configs = JSON.parse(configsJson);
      setSavedConfigs(Object.keys(configs));
    }
  };

  const handleSaveClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    loadSavedConfigNames();
    setSaveDialogOpen(true);
    setConfigName('');
  };

  const handleSaveConfirm = () => {
    if (!configName.trim()) {
      toast.error('Please enter a configuration name');
      return;
    }

    const configsJson = localStorage.getItem('company2_configs');
    const configs = configsJson ? JSON.parse(configsJson) : {};
    
    if (configs[configName.trim()]) {
      setConfigToReplace(configName.trim());
      setReplaceConfirmOpen(true);
      return;
    }

    configs[configName.trim()] = positions;
    localStorage.setItem('company2_configs', JSON.stringify(configs));
    
    setSaveDialogOpen(false);
    setConfigName('');
  };

  const handleReplaceConfig = (configNameToReplace: string) => {
    const configsJson = localStorage.getItem('company2_configs');
    const configs = configsJson ? JSON.parse(configsJson) : {};
    configs[configNameToReplace] = positions;
    localStorage.setItem('company2_configs', JSON.stringify(configs));
    
    setSaveDialogOpen(false);
    setReplaceConfirmOpen(false);
    setConfigName('');
  };

  const handleConfirmReplace = () => {
    if (configToReplace) {
      handleReplaceConfig(configToReplace);
      setConfigToReplace(null);
    }
  };

  const handleLoadClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    loadSavedConfigNames();
    setLoadDialogOpen(true);
  };

  const handleLoadConfig = (name: string) => {
    const configsJson = localStorage.getItem('company2_configs');
    if (configsJson) {
      const configs = JSON.parse(configsJson);
      if (configs[name]) {
        onPositionsChange(configs[name]);
        setLoadDialogOpen(false);
      }
    }
  };

  const standardProfiles = Object.entries(profileTypeMapping).map(([key, value]) => ({
    key,
    name: value.type
  }));

  const assignedMemberIds = getAssignedMemberIds();

  return (
    <>
      <div className="bg-white rounded-lg border border-border">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Company Structure</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Define positions and assign team members
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadClick}
                className="h-9"
              >
                <Upload className="h-4 w-4 mr-2" />
                Load
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveClick}
                className="h-9"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {positions.map((position) => (
            <div key={position.id} className="bg-white rounded-lg border border-border p-6 transition-all hover:shadow-sm relative">
              {/* Two-column grid layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column: Position configuration */}
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor={`position-name-${position.id}`} className="text-sm font-medium text-foreground">
                      Position Name
                    </Label>
                    <Input
                      id={`position-name-${position.id}`}
                      value={position.name}
                      onChange={(e) => updatePosition(position.id, { name: e.target.value })}
                      placeholder="e.g., Sales Manager"
                      className="h-10 rounded-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Profile Type</Label>
                    <RadioGroup
                      value={position.profileSource}
                      onValueChange={(value) => handleProfileSourceChange(position.id, value as ProfileSource)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="ideal" id={`ideal-${position.id}`} />
                        <Label htmlFor={`ideal-${position.id}`} className="font-normal cursor-pointer text-sm">
                          Ideal Profile
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="standard" id={`standard-${position.id}`} />
                        <Label htmlFor={`standard-${position.id}`} className="font-normal cursor-pointer text-sm">
                          Standard Profile
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {position.profileSource === 'ideal' && (
                    <div className="space-y-2">
                      <Label htmlFor={`ideal-profile-${position.id}`} className="text-sm font-medium text-foreground">
                        Select Profile
                      </Label>
                      <Select
                        value={position.idealProfileId || ''}
                        onValueChange={(value) => handleIdealProfileSelect(position.id, value)}
                        disabled={loading}
                      >
                        <SelectTrigger id={`ideal-profile-${position.id}`} className="h-10 rounded-lg">
                          <SelectValue placeholder="Choose an ideal profile..." />
                        </SelectTrigger>
                        <SelectContent>
                          {idealProfiles.map((profile) => (
                            <SelectItem key={profile.id} value={profile.id}>
                              {profile.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {position.profileSource === 'standard' && (
                    <div className="space-y-2">
                      <Label htmlFor={`standard-profile-${position.id}`} className="text-sm font-medium text-foreground">
                        Select Profile
                      </Label>
                      <Select
                        value={position.standardProfileKey || ''}
                        onValueChange={(value) => handleStandardProfileSelect(position.id, value)}
                      >
                        <SelectTrigger id={`standard-profile-${position.id}`} className="h-10 rounded-lg">
                          <SelectValue placeholder="Choose a standard profile..." />
                        </SelectTrigger>
                        <SelectContent>
                          {standardProfiles.map((profile) => (
                            <SelectItem key={profile.key} value={profile.key}>
                              {profile.name} ({profile.key})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {position.resolvedProfile && (
                    <div className="pt-3 border-t border-border">
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Expected Ranges
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs">
                          <span className="font-medium text-foreground">D:</span>
                          <span className="ml-1 text-muted-foreground">
                            {position.resolvedProfile.dominance_min}%-{position.resolvedProfile.dominance_max}%
                          </span>
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs">
                          <span className="font-medium text-foreground">E:</span>
                          <span className="ml-1 text-muted-foreground">
                            {position.resolvedProfile.extraversion_min}%-{position.resolvedProfile.extraversion_max}%
                          </span>
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs">
                          <span className="font-medium text-foreground">P:</span>
                          <span className="ml-1 text-muted-foreground">
                            {position.resolvedProfile.patience_min}%-{position.resolvedProfile.patience_max}%
                          </span>
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs">
                          <span className="font-medium text-foreground">F:</span>
                          <span className="ml-1 text-muted-foreground">
                            {position.resolvedProfile.formality_min}%-{position.resolvedProfile.formality_max}%
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right column: Member assignment */}
                <div className="space-y-4 min-w-0">
                  {position.resolvedProfile ? (
                    <>
                      <div className="flex items-center justify-between pr-8">
                        <Label className="text-sm font-medium text-foreground">
                          Assigned Members
                        </Label>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          {position.assignedMembers?.length || 0}
                        </span>
                      </div>

                      <div className="flex gap-2 items-end">
                        <div className="flex-1 min-w-0">
                          <SearchablePersonSelect
                            submissions={availableMembers.filter(
                              member => !assignedMemberIds.includes(member.id)
                            )}
                            value={selectedMemberIds[position.id] || ''}
                            onValueChange={(value) => {
                              setSelectedMemberIds(prev => ({
                                ...prev,
                                [position.id]: value
                              }));
                            }}
                            placeholder="Search by name, email..."
                            label=""
                            dotColor="bg-primary"
                          />
                        </div>
                        <Button
                          onClick={() => addMemberToPosition(position.id)}
                          disabled={!selectedMemberIds[position.id]}
                          size="sm"
                          className="h-10 px-3 shrink-0"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>

                      {position.assignedMembers && position.assignedMembers.length > 0 ? (
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {position.assignedMembers.map((member) => (
                            <div
                              key={member.memberId}
                              className="flex items-center justify-between p-2.5 rounded-lg border border-border hover:bg-muted/30 transition-colors group"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground truncate">{member.memberName}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {member.email}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMemberFromPosition(position.id, member.memberId)}
                                className="text-muted-foreground hover:text-destructive ml-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                          <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          No members assigned yet
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-[120px] text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                      <div className="text-center p-4">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p>Select a profile to assign team members</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delete position button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removePosition(position.id)}
                className="absolute top-3 right-3 text-muted-foreground hover:text-destructive h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button
            onClick={addPosition}
            variant="outline"
            className="w-full h-11 rounded-lg border-dashed hover:bg-muted/50 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Position
          </Button>
        </div>
      </div>

      {/* Save Configuration Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Save Configuration</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Save your current position setup for later use
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {savedConfigs.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">Replace Existing</Label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {savedConfigs.map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        setConfigToReplace(name);
                        setReplaceConfirmOpen(true);
                      }}
                      className="w-full flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left"
                    >
                      <span className="text-sm font-medium text-foreground">{name}</span>
                      <Save className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label htmlFor="config-name" className="text-sm font-medium text-foreground">
                Save as New
              </Label>
              <div className="space-y-3">
                <Input
                  id="config-name"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  placeholder="e.g., Sales Team Q1"
                  className="h-10 rounded-lg"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveConfirm()}
                />
                <Button
                  onClick={handleSaveConfirm}
                  disabled={!configName.trim()}
                  className="w-full h-10"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Confirmation Dialog */}
      <AlertDialog open={replaceConfirmOpen} onOpenChange={setReplaceConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to replace <span className="font-semibold">"{configToReplace}"</span>? 
              This will overwrite the existing configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReplace}>Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load Configuration Dialog */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Load Configuration</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Select a saved configuration to load
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {savedConfigs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No saved configurations found
              </p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {savedConfigs.map((name) => (
                  <button
                    key={name}
                    onClick={() => handleLoadConfig(name)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left"
                  >
                    <span className="text-sm font-medium text-foreground">{name}</span>
                    <Upload className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
